/* CVO: B2B cart page - quantity, remove, clear cart and order note via the Ajax Cart API. */
if (!customElements.get('cvo-cart')) {
  class CvoCart extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.qtyTimers = {};

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('input', this.onInput.bind(this));
      // Refresh when a related product is added via the theme's quick add.
      this.onThemeCartChange = () => this.refresh();
      document.addEventListener('on:cart:add', this.onThemeCartChange);
      document.addEventListener('on:line-item:change', this.onThemeCartChange);

      this.addEventListener('keydown', (evt) => {
        // Enter in a quantity field updates the line instead of submitting to checkout.
        if (evt.key === 'Enter' && evt.target.matches('.cvo-cart__qty-input')) {
          evt.preventDefault();
          evt.target.blur();
        }
      });
    }

    disconnectedCallback() {
      document.removeEventListener('on:cart:add', this.onThemeCartChange);
      document.removeEventListener('on:line-item:change', this.onThemeCartChange);
    }

    onClick(evt) {
      const qtyBtn = evt.target.closest('[data-cvo-qty]');
      if (qtyBtn) {
        const input = qtyBtn.parentElement.querySelector('.cvo-cart__qty-input');
        const step = Number(input.step) || 1;
        const current = Number(input.value) || 0;
        const next = qtyBtn.dataset.cvoQty === 'plus' ? current + step : Math.max(0, current - step);
        const max = input.max ? Number(input.max) : Infinity;
        input.value = Math.min(next, max);
        this.queueQuantity(input);
        return;
      }

      const remove = evt.target.closest('[data-cvo-remove]');
      if (remove) {
        evt.preventDefault();
        this.changeLine(Number(remove.dataset.cvoRemove), 0);
        return;
      }

      const clear = evt.target.closest('[data-cvo-clear]');
      if (clear) {
        evt.preventDefault();
        const message = this.dataset.clearConfirm;
        if (message && !window.confirm(message)) return;
        this.clearCart();
      }
    }

    onChange(evt) {
      if (evt.target.matches('.cvo-cart__qty-input')) this.queueQuantity(evt.target, 0);
    }

    onInput(evt) {
      if (!evt.target.matches('[data-cvo-note]')) return;
      clearTimeout(this.noteTimer);
      this.noteTimer = setTimeout(() => {
        this.post(this.dataset.cartUpdateUrl, { note: evt.target.value }).catch(() => {});
      }, 500);
    }

    // Debounce so several +/- clicks become one request.
    queueQuantity(input, delay = 400) {
      const line = Number(input.dataset.line);
      clearTimeout(this.qtyTimers[line]);
      this.qtyTimers[line] = setTimeout(() => {
        this.changeLine(line, Math.max(0, parseInt(input.value, 10) || 0));
      }, delay);
    }

    async changeLine(line, quantity) {
      this.setLoading(true);
      try {
        const response = await this.post(this.dataset.cartChangeUrl, {
          line,
          quantity,
          sections: [this.sectionId, 'cart-icon-bubble'],
          sections_url: window.location.pathname
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message);
        this.render(data.sections);
      } catch (error) {
        await this.refresh();
        this.showError(error.message);
      } finally {
        this.setLoading(false);
      }
    }

    async clearCart() {
      this.setLoading(true);
      try {
        await this.post(this.dataset.cartClearUrl, {});
        await this.refresh();
      } catch (error) {
        this.showError(error.message);
      } finally {
        this.setLoading(false);
      }
    }

    async refresh() {
      const url = `${window.location.pathname}?sections=${this.sectionId},cart-icon-bubble`;
      const response = await fetch(url);
      if (response.ok) this.render(await response.json());
    }

    render(sections) {
      if (!sections) return;

      const sectionHtml = sections[this.sectionId];
      if (sectionHtml) {
        const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
        const fresh = doc.querySelector('cvo-cart');
        if (fresh) this.innerHTML = fresh.innerHTML;
      }

      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble && sections['cart-icon-bubble'] !== undefined) {
        const doc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
        const source = doc.querySelector('.shopify-section') || doc.body;
        bubble.innerHTML = source.innerHTML;
      }

      document.dispatchEvent(new CustomEvent('cvo:cart:updated', { bubbles: true }));
    }

    post(url, body) {
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      });
    }

    setLoading(isLoading) {
      this.classList.toggle('is-loading', isLoading);
      this.setAttribute('aria-busy', String(isLoading));
    }

    showError(message) {
      const box = this.querySelector('.cvo-cart__error');
      if (!box || !message) return;
      box.textContent = message;
      box.hidden = false;
    }
  }

  customElements.define('cvo-cart', CvoCart);
}
